import React, { useState, useEffect } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { useCart } from '../contexts/CartContext';
import { dataService } from '../services/dataService';
import { formatCurrency, getUserCurrency, formatCurrencySync, getCurrencyByLocation, setUserCurrency as saveUserCurrency } from '../shared/utils/currency';
import useCurrencyCache from '../hooks/useCurrencyCache';
import { authService } from '../config/supabase';
// Navigation and bottom tabs are hidden on detail screen to match design
import './CustomerFoodDetail.css';

const CustomerFoodDetail = () => {
  const { id, type } = useParams();
  
  // Use currency cache hook
  const { formatCurrency: formatCurrencyCached } = useCurrencyCache();
  const [quantity, setQuantity] = useState(1);
  const [userCurrency, setUserCurrency] = useState('PKR'); // Location-based currency
  const [selectedOptions, setSelectedOptions] = useState([]);
  const [showCartModal, setShowCartModal] = useState(false);
  const [baseItem, setBaseItem] = useState(null);
  const [loading, setLoading] = useState(true);
  const { addToCart } = useCart();
  const navigate = useNavigate();

  // Load user location and currency
  useEffect(() => {
    const loadUserCurrency = async () => {
      try {
        const currentUser = await authService.getCurrentUser();
        const email = currentUser?.email;
        const meta = currentUser?.user_metadata || {};

        let city = '';
        let country = '';
        let currency = '';

        if (email) {
          const customerRecord = await dataService.getCustomerByEmail(email).catch(() => null);
          if (customerRecord) {
            city = customerRecord.city || '';
            country = customerRecord.country || '';
            currency = customerRecord.currency || '';
          }
        }

        if (!city) city = meta.city || '';
        if (!country) country = meta.country || '';
        if (!currency) currency = meta.currency || '';

        // Get currency from location if not set
        const derived = await getCurrencyByLocation(country, city);
        const finalCurrency = currency || derived || getUserCurrency();
        setUserCurrency(finalCurrency);
        try { saveUserCurrency(finalCurrency); } catch (_) {}
      } catch (error) {
        console.error('Error loading user currency:', error);
        setUserCurrency(getUserCurrency());
      }
    };

    loadUserCurrency();
  }, []);

  // Load item data
  useEffect(() => {
    const loadItem = async () => {
      try {
        setLoading(true);
        let item = null;
        if (type === 'grocery') {
          const groceryItems = await dataService.getGroceryItems();
          item = groceryItems.find(m => String(m.id) === String(id));
        } else if (type === 'pharmacy') {
          const items = await dataService.getPharmacyItems();
          item = items.find(m => String(m.id) === String(id));
        } else if (type === 'food') {
          const foodItems = await dataService.getFoodItems();
          item = foodItems.find(m => String(m.id) === String(id));
        } else if (type === 'clothing') {
          const clothingItems = await dataService.getClothingItems();
          item = clothingItems.find(m => String(m.id) === String(id));
        } else if (type === 'accessories') {
          const accessoriesItems = await dataService.getAccessoriesItems();
          item = accessoriesItems.find(m => String(m.id) === String(id));
        } else if (type === 'electronics') {
          const electronicsItems = await dataService.getElectronicsItems();
          item = electronicsItems.find(m => String(m.id) === String(id));
        } else {
          // Fallback: try all types
          const foodItems = await dataService.getFoodItems();
          item = foodItems.find(m => String(m.id) === String(id));
          if (!item) {
            const groceryItems = await dataService.getGroceryItems();
            item = groceryItems.find(m => String(m.id) === String(id));
          }
          if (!item) {
            const pItems = await dataService.getPharmacyItems();
            item = pItems.find(m => String(m.id) === String(id));
          }
          if (!item) {
            const clothingItems = await dataService.getClothingItems();
            item = clothingItems.find(m => String(m.id) === String(id));
          }
          if (!item) {
            const accessoriesItems = await dataService.getAccessoriesItems();
            item = accessoriesItems.find(m => String(m.id) === String(id));
          }
          if (!item) {
            const electronicsItems = await dataService.getElectronicsItems();
            item = electronicsItems.find(m => String(m.id) === String(id));
          }
        }
        setBaseItem(item);
      } catch (error) {
        console.error('Error loading item:', error);
        setBaseItem(null);
      } finally {
        setLoading(false);
      }
    };

    loadItem();
  }, [id, type]);

  const foodItem = baseItem ? {
    ...baseItem,
    options: (baseItem.type === 'food' || type === 'food') ? [
      { id: 1, name: 'Coke', price: 1.99, image: 'https://images.pexels.com/photos/50593/coca-cola-cold-drink-soft-drink-coke-50593.jpeg?auto=compress&cs=tinysrgb&w=200' },
      { id: 2, name: 'Latte', price: 2.49, image: 'https://images.pexels.com/photos/302899/pexels-photo-302899.jpeg?auto=compress&cs=tinysrgb&w=200' },
      { id: 3, name: 'Mocktail', price: 2.99, image: 'https://images.pexels.com/photos/1283219/pexels-photo-1283219.jpeg?auto=compress&cs=tinysrgb&w=200' },
      { id: 4, name: 'Fries', price: 2.49, image: 'https://images.pexels.com/photos/1583884/pexels-photo-1583884.jpeg?auto=compress&cs=tinysrgb&w=200' }
    ] : []
  } : null;

  const handleOptionChange = (optionId) => {
    setSelectedOptions(prev => 
      prev.includes(optionId) 
        ? prev.filter(id => id !== optionId)
        : [...prev, optionId]
    );
  };

  const calculateTotalPrice = () => {
    const basePrice = foodItem.price * quantity;
    const optionsPrice = selectedOptions.reduce((total, optionId) => {
      const option = foodItem.options.find(opt => opt.id === optionId);
      return total + (option ? option.price : 0);
    }, 0);
    return (basePrice + optionsPrice).toFixed(2);
  };

  const handleAddToCart = () => {
    const selectedOptionsData = selectedOptions.map(optionId => 
      foodItem.options.find(opt => opt.id === optionId)
    );
    
    const cartItem = {
      id: foodItem.id,
      name: foodItem.name,
      price: parseFloat(calculateTotalPrice()),
      image: foodItem.image,
      quantity: quantity,
      options: selectedOptionsData,
      type: (type === 'grocery') ? 'grocery' : (type === 'pharmacy' ? 'pharmacy' : 'food')
    };
    
    console.log('Adding to cart:', cartItem);
    addToCart(cartItem);
    setShowCartModal(true);
  };

  const handleGoToCart = () => {
    setShowCartModal(false);
    navigate('/cart');
  };

  const handleContinueShopping = () => {
    setShowCartModal(false);
  };

  const increaseQuantity = () => {
    setQuantity(prev => prev + 1);
  };

  const decreaseQuantity = () => {
    if (quantity > 1) {
      setQuantity(prev => prev - 1);
    }
  };

  if (loading) {
    return (
      <div className="App">
        <div className="food-detail-container">
          <div className="loading">Loading...</div>
        </div>
      </div>
    );
  }

  if (!foodItem) {
    return (
      <div className="App">
        <div className="food-detail-container">
          <div className="error">Item not found</div>
        </div>
      </div>
    );
  }

  return (
    <div className="App">
      
      <div className="food-detail-container">
        <div className="food-detail-content">
          <div className="food-detail-card">
            <div className="detail-hero">
              <button className="back-btn" onClick={() => window.history.back()} aria-label="Back">←</button>
              <div className="hero-plate">
                <img src={foodItem.image} alt={foodItem.name} />
              </div>
            </div>
            
            <div className="detail-sheet">
              <div className="sheet-header-row">
                <span className="rating-chip"><span className="star">★</span> {foodItem.rating ? Number(foodItem.rating).toFixed(1) : 'New'}</span>
                <span className="price-chip">{formatCurrencyCached(foodItem.price, userCurrency)}</span>
              </div>

              <div className="title-row">
                <h1 className="food-detail-title">{foodItem.name}</h1>
                <div className="mini-qty">
                  <span className="dot" />
                  <span className="count">{quantity}</span>
                  <button className="mini-plus" onClick={increaseQuantity}>+</button>
                </div>
              </div>

              <p className="food-detail-description">{foodItem.description}</p>

              {foodItem.type === 'food' && foodItem.options.length > 0 && (
                <div className="food-detail-options">
                  <h3 className="options-title">Add Ons</h3>
                  <div className="addons-row">
                    {foodItem.options.slice(0,3).map((option) => {
                      const isSelected = selectedOptions.includes(option.id);
                      return (
                        <div key={option.id} className={`addon-card ${isSelected ? 'selected' : ''}`}>
                          <div className="addon-thumb">
                            <img src={option.image} alt={option.name} />
                          </div>
                          <div className="addon-info">
                            <span className="addon-name">{option.name}</span>
                            <span className="addon-price">${option.price}</span>
                          </div>
                          <button 
                            className={`addon-btn ${isSelected ? 'remove' : 'add'}`} 
                            onClick={() => handleOptionChange(option.id)}
                          >
                            {isSelected ? '−' : '+'}
                          </button>
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}

              <div className="quantity-section">
                <h3 className="quantity-title">Quantity</h3>
                <div className="quantity-controls">
                  <button 
                    className="quantity-btn"
                    onClick={decreaseQuantity}
                    disabled={quantity <= 1}
                  >
                    -
                  </button>
                  <span className="quantity-display">{quantity}</span>
                  <button 
                    className="quantity-btn"
                    onClick={increaseQuantity}
                  >
                    +
                  </button>
                </div>
              </div>

              <div className="add-to-cart-section">
                <button className="add-to-cart-btn" onClick={handleAddToCart}>Add to Cart</button>
                
                <Link to="/menu" className="back-to-menu-btn">
                  Back to Menu
                </Link>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Cart Success Modal */}
      {showCartModal && (
        <div className="cart-success-modal-overlay" onClick={handleContinueShopping}>
          <div className="cart-success-modal" onClick={(e) => e.stopPropagation()}>
            <div className="cart-success-icon">✅</div>
            <h3>Item Added to Cart!</h3>
            <p>You've added <strong>{foodItem.name}</strong> to your cart.</p>
            <div className="cart-success-actions">
              <button className="cart-success-action-btn" onClick={handleGoToCart}>Go to Cart</button>
              <button className="cart-success-action-btn" onClick={handleContinueShopping}>Continue Shopping</button>
            </div>
          </div>
        </div>
      )}
      
    </div>
  );
};

export default CustomerFoodDetail;
